"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateDashboardLayoutWidgetSize = exports.getLayoutWithoutGridHeights = exports.getDashboardLayoutWidgetMaxGridHeight = exports.getDashboardLayoutWidgetMinGridHeight = exports.getDashboardLayoutWidgetDefaultHeight = exports.getDashboardLayoutWidgetDefaultGridWidth = exports.getDashboardLayoutWidgetMinGridWidth = exports.getDashboardLayoutItemMaxGridWidth = exports.getDashboardLayoutItemHeightForGrid = exports.getDashboardLayoutItemHeightForRatioAndScreen = exports.getDashboardLayoutItemHeight = exports.getResizedItemPositions = exports.splitDashboardLayoutItemsAsRenderedGridRows = exports.unifyDashboardLayoutItemHeights = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var flatten_1 = tslib_1.__importDefault(require("lodash/flatten"));
var round_1 = tslib_1.__importDefault(require("lodash/round"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var clamp_1 = tslib_1.__importDefault(require("lodash/clamp"));
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var constants_1 = require("../constants");
var layout_1 = require("../facade/layout");
var layout_2 = require("./../builder/layout");
var VisualizationCatalog_1 = require("../../../components/VisualizationCatalog");
var sdk_model_1 = require("@gooddata/sdk-model");
var FluidLayoutDescriptor_1 = require("../FluidLayoutDescriptor");
var constants_2 = require("../../../components/pluggableVisualizations/constants");
function unifyDashboardLayoutItemHeights(itemsOrLayout) {
    if (sdk_backend_spi_1.isDashboardLayout(itemsOrLayout)) {
        // eslint-disable-next-line sonarjs/prefer-immediate-return
        var updatedLayout = tslib_1.__assign(tslib_1.__assign({}, itemsOrLayout), { sections: layout_1.DashboardLayoutFacade.for(itemsOrLayout)
                .sections()
                .reduce(function (acc, section) {
                return tslib_1.__spreadArrays(acc, [
                    tslib_1.__assign(tslib_1.__assign({}, section.raw()), { items: unifyDashboardLayoutItemHeights(section.items().raw()) }),
                ]);
            }, []) });
        return updatedLayout;
    }
    var itemsWithSizeForAllScreens = itemsOrLayout.map(function (item) { return (tslib_1.__assign(tslib_1.__assign({}, item), { size: implicitLayoutItemSizeFromXlSize(item.size.xl) })); });
    return constants_1.ALL_SCREENS.reduce(function (acc, screen) {
        var itemsAsFutureGridRows = splitDashboardLayoutItemsAsRenderedGridRows(acc, screen);
        return flatten_1.default(itemsAsFutureGridRows.map(function (futureGridRow) {
            return unifyDashboardLayoutItemHeightsForScreen(futureGridRow, screen);
        }));
    }, itemsWithSizeForAllScreens);
}
exports.unifyDashboardLayoutItemHeights = unifyDashboardLayoutItemHeights;
/**
 * Derive dashboard layout size for all screens from dashboard layout size defined for xl screen.
 *
 * @param xlSize - dashboard layout size for xl screen
 */
function implicitLayoutItemSizeFromXlSize(xlSize) {
    var xlWidth = xlSize.gridWidth;
    var xlHeight = xlSize.gridHeight;
    var ratio = xlSize.heightAsRatio;
    switch (xlWidth) {
        case 0:
            return dashboardLayoutItemSizeForAllScreens(0, 0, 0, 0, 0, 0, 0);
        case 1:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 2, 6, 12);
        case 2:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 4, 6, 12);
        case 3:
        case 4:
        case 5:
        case 6:
        case 7:
        case 8:
        case 9:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 6, 12, 12);
        case 10:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
        case 11:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
        case 12:
            return dashboardLayoutItemSizeForAllScreens(ratio, xlHeight, xlWidth, xlWidth, 12, 12, 12);
    }
}
/**
 * Create dashboard layout item size for all screens,
 * with identical height, defined as ratio,
 * but different width, defined as grid items count.
 *
 * @param heightAsRatio - height as ratio to the width, defined in percents
 * @param gridHeight - height as number of grid rows
 * @param xl - width as grid items count for xl screen
 * @param lg - width as grid items count for lg screen
 * @param md - width as grid items count for md screen
 * @param sm - width as grid items count for sm screen
 * @param xs - width as grid items count for xs screen
 */
function dashboardLayoutItemSizeForAllScreens(heightAsRatio, gridHeight, xl, lg, md, sm, xs) {
    if (gridHeight) {
        return {
            xl: {
                gridWidth: xl,
                gridHeight: gridHeight,
            },
            lg: {
                gridWidth: lg,
                gridHeight: gridHeight,
            },
            md: {
                gridWidth: md,
                gridHeight: gridHeight,
            },
            sm: {
                gridWidth: sm,
                gridHeight: gridHeight,
            },
            xs: {
                gridWidth: xs,
                gridHeight: gridHeight,
            },
        };
    }
    return {
        xl: {
            gridWidth: xl,
            heightAsRatio: heightAsRatio,
        },
        lg: {
            gridWidth: lg,
            heightAsRatio: heightAsRatio,
        },
        md: {
            gridWidth: md,
            heightAsRatio: heightAsRatio,
        },
        sm: {
            gridWidth: sm,
            heightAsRatio: heightAsRatio,
        },
        xs: {
            gridWidth: xs,
            heightAsRatio: heightAsRatio,
        },
    };
}
/**
 * Divide the items into a list representing the future rows of the grid.
 * This is useful for performing item transformations, depending on how they really appear in the grid.
 *
 * @param items - dashboard layout items
 * @param screen - responsive screen class
 */
function splitDashboardLayoutItemsAsRenderedGridRows(items, screen) {
    var renderedRows = [];
    var currentRowWidth = 0;
    var currentRow = [];
    items.forEach(function (item) {
        var itemSize = item.size[screen];
        if (isNil_1.default(itemSize)) {
            throw Error("Item size for current screen is undefined");
        }
        if (currentRowWidth + itemSize.gridWidth > constants_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT) {
            renderedRows.push(currentRow);
            currentRow = [];
            currentRowWidth = 0;
        }
        currentRow.push(item);
        currentRowWidth = currentRowWidth + itemSize.gridWidth;
    });
    if (currentRow.length > 0) {
        renderedRows.push(currentRow);
    }
    return renderedRows;
}
exports.splitDashboardLayoutItemsAsRenderedGridRows = splitDashboardLayoutItemsAsRenderedGridRows;
/**
 * Calculate dashboard layout item height for the provided screen.
 * Result, if custom height is defined, is height of the item, defined
 * as grid items count, multiplied by {@link GRID_ROW_HEIGHT_IN_PX} or width of the item,
 * defined as grid items count, multiplied by height, defined as a ratio.
 *
 * @param item - dashboard layout item
 * @param screen - responsive screen class
 */
function dashboardLayoutItemHeightForScreen(item, screen) {
    var _a, _b;
    var _c = (_b = (_a = item.size) === null || _a === void 0 ? void 0 : _a[screen]) !== null && _b !== void 0 ? _b : {}, gridWidth = _c.gridWidth, gridHeight = _c.gridHeight, _d = _c.heightAsRatio, heightAsRatio = _d === void 0 ? 0 : _d;
    if (!gridWidth) {
        return 0;
    }
    if (gridHeight) {
        return exports.getDashboardLayoutItemHeightForGrid(gridHeight);
    }
    return gridWidth * heightAsRatio;
}
/**
 * Unify dashboard layout items height, defined as ratio, for the provided screen.
 * It overrides height of all items to the highest item height found for the provided screen.
 *
 * @param items - dashboard layout items
 * @param screen -  responsive screen class
 */
function unifyDashboardLayoutItemHeightsForScreen(items, screen) {
    var heights = items.map(function (item) { return dashboardLayoutItemHeightForScreen(item, screen); });
    var maxHeight = Math.max.apply(Math, tslib_1.__spreadArrays([0], heights));
    if (maxHeight === 0) {
        return items;
    }
    return items.map(function (item) { return updateDashboardLayoutItemHeight(item, screen, maxHeight); });
}
var updateDashboardLayoutItemHeight = function (item, screen, maxHeight) {
    var _a, _b;
    var itemSizeForCurrentScreen = item.size[screen];
    var heightAsRatio = (itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.gridWidth) ? round_1.default(maxHeight / itemSizeForCurrentScreen.gridWidth, 2)
        : 0;
    var updatedColumn = item;
    if (!(itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.gridHeight) &&
        !isNil_1.default(itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.heightAsRatio) &&
        (itemSizeForCurrentScreen === null || itemSizeForCurrentScreen === void 0 ? void 0 : itemSizeForCurrentScreen.heightAsRatio) !== heightAsRatio) {
        if (sdk_backend_spi_1.isWidget(updatedColumn.widget) || sdk_backend_spi_1.isWidgetDefinition(updatedColumn.widget)) {
            updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { widget: tslib_1.__assign({}, updatedColumn.widget) });
        }
        updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { size: tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size), (_a = {}, _a[screen] = tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size[screen]), { heightAsRatio: heightAsRatio }), _a)) });
    }
    if (screen === "xs" && heightAsRatio > constants_1.DASHBOARD_LAYOUT_MAX_HEIGHT_AS_RATIO_XS) {
        updatedColumn = tslib_1.__assign(tslib_1.__assign({}, updatedColumn), { size: tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size), (_b = {}, _b[screen] = tslib_1.__assign(tslib_1.__assign({}, updatedColumn.size[screen]), { heightAsRatio: constants_1.DASHBOARD_LAYOUT_MAX_HEIGHT_AS_RATIO_XS }), _b)) });
    }
    return updatedColumn;
};
/**
 *
 * @internal
 */
exports.getResizedItemPositions = function (originalLayout, resizedLayout, positions) {
    if (positions === void 0) { positions = []; }
    var originalLayoutFacade = layout_1.DashboardLayoutFacade.for(originalLayout);
    return layout_1.DashboardLayoutFacade.for(resizedLayout)
        .sections()
        .reduce(function (acc, section) {
        return section.items().reduce(function (acc, item) {
            var originalColumn = originalLayoutFacade
                .sections()
                .section(section.index())
                .items()
                .item(item.index());
            var originalContent = originalColumn.widget();
            var updatedContent = item.widget();
            // Is nested layout?
            if (sdk_backend_spi_1.isDashboardLayout(originalContent) && sdk_backend_spi_1.isDashboardLayout(updatedContent)) {
                return exports.getResizedItemPositions(originalContent, updatedContent, positions);
            }
            if (!isEqual_1.default(originalColumn.size(), item.size()) &&
                (sdk_backend_spi_1.isWidget(updatedContent) || sdk_backend_spi_1.isWidgetDefinition(updatedContent))) {
                acc.push([item.section().index(), item.index()]);
            }
            return acc;
        }, acc);
    }, positions);
};
exports.getDashboardLayoutItemHeight = function (size) {
    var gridHeight = size.gridHeight;
    if (gridHeight) {
        return exports.getDashboardLayoutItemHeightForGrid(gridHeight);
    }
    return undefined;
};
exports.getDashboardLayoutItemHeightForRatioAndScreen = function (size, screen) {
    var gridWidth = size.gridWidth, heightAsRatio = size.heightAsRatio;
    var actualWidth = constants_1.DASHBOARD_LAYOUT_CONTAINER_WIDTHS[screen];
    var actualColumnUnitWidth = actualWidth / constants_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT;
    return actualColumnUnitWidth * gridWidth * (heightAsRatio / 100);
};
exports.getDashboardLayoutItemHeightForGrid = function (gridHeight) {
    return gridHeight * constants_1.GRID_ROW_HEIGHT_IN_PX;
};
function getDashboardLayoutItemMaxGridWidth(item, screen) {
    var _a;
    var gridRowWidth = 0;
    var sectionItems = item.section().items().all();
    for (var _i = 0, sectionItems_1 = sectionItems; _i < sectionItems_1.length; _i++) {
        var sectionItem = sectionItems_1[_i];
        var newWidth = sectionItem.sizeForScreen(screen).gridWidth + gridRowWidth;
        if (newWidth <= constants_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT) {
            if (sectionItem.index() === item.index()) {
                break;
            }
            gridRowWidth = newWidth;
        }
        else {
            if (sectionItem.index() === item.index()) {
                return constants_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT;
            }
            gridRowWidth = (_a = sectionItem.sizeForScreen(screen)) === null || _a === void 0 ? void 0 : _a.gridWidth;
        }
    }
    return constants_1.DASHBOARD_LAYOUT_GRID_COLUMNS_COUNT - gridRowWidth;
}
exports.getDashboardLayoutItemMaxGridWidth = getDashboardLayoutItemMaxGridWidth;
var getSizeInfo = function (settings, widgetType, widgetContent) {
    if (widgetType === "kpi") {
        return getKpiSizeInfo(settings, widgetContent);
    }
    return getVisualizationSizeInfo(settings, widgetContent);
};
var getVisualizationSizeInfo = function (settings, insight) {
    var sizeInfo;
    if (sdk_model_1.isInsight(insight)) {
        sizeInfo = VisualizationCatalog_1.FullVisualizationCatalog.forInsight(insight).getSizeInfo(insight, FluidLayoutDescriptor_1.fluidLayoutDescriptor, settings);
    }
    if (!sizeInfo) {
        if (!settings.enableKDWidgetCustomHeight) {
            return constants_2.INSIGHT_WIDGET_SIZE_INFO_DEFAULT_LEGACY;
        }
        return constants_2.INSIGHT_WIDGET_SIZE_INFO_DEFAULT;
    }
    return sizeInfo;
};
var getKpiSizeInfo = function (settings, kpi) {
    if (!settings.enableKDWidgetCustomHeight) {
        return constants_2.KPI_WIDGET_SIZE_INFO_DEFAULT_LEGACY;
    }
    if (!sdk_backend_spi_1.isLegacyKpi(kpi)) {
        return constants_2.KPI_WIDGET_SIZE_INFO_DEFAULT;
    }
    return {
        width: {
            min: 2,
            default: 2,
        },
        height: sdk_backend_spi_1.isLegacyKpiWithoutComparison(kpi)
            ? {
                default: 8,
                min: 6,
                max: 40,
            }
            : {
                default: 11,
                min: 10,
                max: 40,
            },
    };
};
function getDashboardLayoutWidgetMinGridWidth(settings, widgetType, widgetContent) {
    var sizeInfo = getSizeInfo(settings, widgetType, widgetContent);
    return sizeInfo.width.min;
}
exports.getDashboardLayoutWidgetMinGridWidth = getDashboardLayoutWidgetMinGridWidth;
function getDashboardLayoutWidgetDefaultGridWidth(settings, widgetType, widgetContent) {
    var sizeInfo = getSizeInfo(settings, widgetType, widgetContent);
    return sizeInfo.width.default;
}
exports.getDashboardLayoutWidgetDefaultGridWidth = getDashboardLayoutWidgetDefaultGridWidth;
function getDashboardLayoutWidgetDefaultHeight(settings, widgetType, widgetContent) {
    var sizeInfo = getSizeInfo(settings, widgetType, widgetContent);
    return FluidLayoutDescriptor_1.fluidLayoutDescriptor.toHeightInPx(sizeInfo.height.default);
}
exports.getDashboardLayoutWidgetDefaultHeight = getDashboardLayoutWidgetDefaultHeight;
function getDashboardLayoutWidgetMinGridHeight(settings, widgetType, widgetContent) {
    var sizeInfo = getSizeInfo(settings, widgetType, widgetContent);
    return sizeInfo.height.min;
}
exports.getDashboardLayoutWidgetMinGridHeight = getDashboardLayoutWidgetMinGridHeight;
function getDashboardLayoutWidgetMaxGridHeight(settings, widgetType, widgetContent) {
    var sizeInfo = getSizeInfo(settings, widgetType, widgetContent);
    return sizeInfo.height.max;
}
exports.getDashboardLayoutWidgetMaxGridHeight = getDashboardLayoutWidgetMaxGridHeight;
function getLayoutWithoutGridHeights(layout) {
    var layoutBuilder = layout_2.DashboardLayoutBuilder.for(layout);
    return layoutBuilder
        .modifySections(function (section) {
        return section.modifyItems(function (itemBuilder, itemFacade) {
            var widget = itemFacade.widget();
            if (sdk_backend_spi_1.isDashboardLayout(widget)) {
                var updatedLayout = getLayoutWithoutGridHeights(widget);
                return itemBuilder.widget(updatedLayout);
            }
            var itemWithoutGridHeight = removeGridHeightFromItemSize(itemFacade.raw());
            return itemBuilder.setItem(itemWithoutGridHeight);
        });
    })
        .build();
}
exports.getLayoutWithoutGridHeights = getLayoutWithoutGridHeights;
function removeGridHeightFromItemSize(item) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = item.size.xl, gridHeight = _a.gridHeight, rest = tslib_1.__rest(_a, ["gridHeight"]);
    return tslib_1.__assign(tslib_1.__assign({}, item), { size: tslib_1.__assign(tslib_1.__assign({}, item.size), { xl: tslib_1.__assign({}, rest) }) });
}
/**
 * @internal
 */
function validateDashboardLayoutWidgetSize(currentWidth, currentHeight, widgetType, widgetContent, settings) {
    var minWidth = getDashboardLayoutWidgetMinGridWidth(settings, widgetType, widgetContent);
    var maxWidth = FluidLayoutDescriptor_1.fluidLayoutDescriptor.gridColumnsCount;
    var minHeight = getDashboardLayoutWidgetMinGridHeight(settings, widgetType, widgetContent);
    var maxHeight = getDashboardLayoutWidgetMaxGridHeight(settings, widgetType, widgetContent);
    var validWidth = currentWidth !== undefined ? clamp_1.default(currentWidth, minWidth, maxWidth) : currentWidth;
    var validHeight = currentHeight !== undefined ? clamp_1.default(currentHeight, minHeight, maxHeight) : currentHeight;
    return {
        validWidth: validWidth,
        validHeight: validHeight,
    };
}
exports.validateDashboardLayoutWidgetSize = validateDashboardLayoutWidgetSize;
//# sourceMappingURL=sizing.js.map